import React from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import { activityApi } from "../../api/activity";
import { projectsApi } from "../../api/projects";
import { productsApi } from "../../api/products";
import { settingsApi } from "../../api/settings";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { buildProjectActionTasks } from "../../utils/projectActionTasks";
import { getProjectBillingAccess } from "../../utils/projectBillingAccess";
import { normalizeProjectModules } from "../../utils/projectModules";
import { isSuperAdmin } from "../../utils/roles";
import { buildTrialMissions } from "../../utils/trialOnboarding";
import { isIntensiveProjectOnboarding } from "../../utils/projectExperience";

const BRAND = { orange: "#F35A3A", dark: "#19242F", green: "#26A269" };
const PANEL_WIDTH = 390;

const ACTIVITY_VIEWS = [
  { id: "tasks", label: "Tareas" },
  { id: "notices", label: "Avisos" },
  { id: "updates", label: "Novedades" },
];

const OPERATIONAL_CATEGORY_LABELS = {
  order: "Pedidos",
  appointment: "Reservas",
  customer: "Clientes",
  payment: "Cobros",
  erp: "Gestión",
  cash: "Caja",
  stock: "Stock",
  product: "Catálogo",
  billing: "Vencimientos",
  membership: "Membresías",
  subscription: "Membresías",
  integration: "Integraciones",
};

const OPERATIONAL_CATEGORIES = new Set(Object.keys(OPERATIONAL_CATEGORY_LABELS));

function projectKey(project) {
  return project?.slug || project?.id || project?._id || "";
}

function readStorageKey(project) {
  return `webprofit:global-activity-read:${projectKey(project) || "account"}`;
}

function readLastSeen(project) {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(readStorageKey(project));
    const parsed = Number(raw || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeLastSeen(project) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(readStorageKey(project), String(Date.now()));
  } catch {
    // La campana sigue funcionando aunque el almacenamiento local esté bloqueado.
  }
}

function eventTimestamp(event) {
  const parsed = new Date(event?.occurredAt || event?.createdAt || 0).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function relativeTime(value) {
  const timestamp = new Date(value || 0).getTime();
  if (!Number.isFinite(timestamp) || timestamp <= 0) return "";
  const diffMinutes = Math.max(0, Math.round((Date.now() - timestamp) / 60000));
  if (diffMinutes < 1) return "Ahora";
  if (diffMinutes < 60) return `Hace ${diffMinutes} min`;
  const hours = Math.floor(diffMinutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days} d`;
}

function categoryLabel(category) {
  const labels = {
    landing: "Página",
    product: "Productos",
    order: "Pedidos",
    payment: "Pagos",
    appointment: "Reservas",
    customer: "Clientes",
    membership: "Membresías",
    subscription: "Membresías",
    erp: "Gestión",
    cash: "Caja",
    stock: "Stock",
    billing: "Suscripción",
    push: "Push",
    project: "Proyecto",
    user: "Cuenta",
    commercial: "Comercial",
    system: "Sistema",
  };
  return labels[category] || "Actividad";
}

function eventRoute(project, event) {
  const key = projectKey(project);
  if (!key) return "/dashboard";
  const base = `/dashboard/proyectos/${key}`;
  const category = String(event?.category || "").toLowerCase();
  if (category === "landing") return `${base}/pagina`;
  if (category === "product") return `${base}/productos`;
  if (category === "order") return `${base}/pedidos`;
  if (category === "appointment") return `${base}/agenda`;
  if (["payment", "erp", "cash", "stock"].includes(category)) return `${base}/sistema`;
  if (category === "billing") return `${base}/configuracion/suscripcion`;
  if (category === "push") return `${base}/configuracion/notificaciones`;
  return `${base}/actividad`;
}

function eventState(event) {
  const severity = String(event?.severity || "info").toLowerCase();
  const action = String(event?.action || "").toLowerCase();

  if (["critical", "warning"].includes(severity)) {
    return { label: "Revisar", color: "warning", priority: "Alta" };
  }
  if (action.includes("failed") || action.includes("rejected") || action.includes("cancel")) {
    return { label: "Revisar", color: "warning", priority: "Alta" };
  }
  if (action.includes("created") || action.includes("confirmed") || action.includes("new")) {
    return { label: "Nuevo", color: "info", priority: "Normal" };
  }
  if (action.includes("completed") || action.includes("paid") || action.includes("closed")) {
    return { label: "Completado", color: "success", priority: "Normal" };
  }
  if (action.includes("updated") || action.includes("changed")) {
    return { label: "Actualizado", color: "default", priority: "Normal" };
  }
  return {
    label: severity === "success" ? "Registrado" : "Actividad",
    color: severity === "success" ? "success" : "default",
    priority: "Normal",
  };
}

function isOperationalEvent(event) {
  const category = String(event?.category || "").toLowerCase();
  const severity = String(event?.severity || "").toLowerCase();
  return OPERATIONAL_CATEGORIES.has(category) || ["warning", "critical"].includes(severity);
}

function emptyMessage(view, activeProject, loading) {
  if (!activeProject) return "Elegí o creá un proyecto para ver su actividad.";
  if (loading) return "Actualizando actividad…";
  if (view === "tasks") return "No hay tareas pendientes detectadas para este proyecto.";
  if (view === "notices") return "No hay avisos que requieran atención ahora.";
  return "Todavía no hay novedades operativas recientes para mostrar.";
}

function missionRoute(project, mission) {
  const key = projectKey(project);
  if (!key) return "/dashboard/proyectos";
  if (mission?.section === "products") return `/dashboard/proyectos/${key}/productos?crear=1`;
  return `/dashboard/proyectos/${key}/pagina?mision=${mission?.routeKey || "datos"}`;
}

function sectionTitle(icon, title, count) {
  return (
    <Stack direction="row" spacing={0.8} alignItems="center">
      {icon}
      <Typography sx={{ fontSize: ".76rem", fontWeight: 1000, letterSpacing: .35, color: BRAND.dark }}>
        {title}
      </Typography>
      {Number.isFinite(count) ? (
        <Chip size="small" label={count} sx={{ height: 20, fontSize: ".64rem", fontWeight: 950 }} />
      ) : null}
    </Stack>
  );
}

export default function GlobalActivityBell({ mobile = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeProject } = useProjects();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [view, setView] = React.useState("tasks");
  const [events, setEvents] = React.useState([]);
  const [missions, setMissions] = React.useState([]);
  const [readiness, setReadiness] = React.useState(() => activeProject?.readiness || null);
  const [loading, setLoading] = React.useState(false);
  const [lastSeen, setLastSeen] = React.useState(() => readLastSeen(activeProject));
  const requestVersionRef = React.useRef(0);

  const superAdmin = isSuperAdmin(user);
  const key = projectKey(activeProject);
  const modules = React.useMemo(
    () => normalizeProjectModules(activeProject?.modules),
    [activeProject?.modules],
  );
  const billingAccess = getProjectBillingAccess(activeProject);
  const needsMissionFallback = Boolean(activeProject)
    && !superAdmin
    && isIntensiveProjectOnboarding(activeProject);
  const open = Boolean(anchorEl);

  const load = React.useCallback(async () => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    setLoading(true);

    try {
      if (!activeProject || !key) {
        setEvents([]);
        setMissions([]);
        return;
      }

      const activityRequest = activityApi.project(key, { page: 1, pageSize: 8 });
      const readinessRequest = typeof projectsApi.getReadiness === "function"
        ? projectsApi.getReadiness(key)
        : Promise.reject(new Error("PROJECT_READINESS_API_UNAVAILABLE"));
      const missionRequests = needsMissionFallback
        ? [
            modules.products ? productsApi.list(key) : Promise.resolve({ products: [] }),
            modules.landing ? settingsApi.get(key) : Promise.resolve({ settings: {} }),
          ]
        : [];

      const results = await Promise.allSettled([
        activityRequest,
        readinessRequest,
        ...missionRequests,
      ]);
      if (requestVersionRef.current !== requestVersion) return;

      const activityResult = results[0];
      const readinessResult = results[1];
      const missionResults = results.slice(2);
      const activityData = activityResult.status === "fulfilled" ? activityResult.value : {};
      setEvents(Array.isArray(activityData?.items) ? activityData.items : []);

      const nextReadiness = readinessResult.status === "fulfilled"
        ? readinessResult.value?.readiness || null
        : activeProject?.readiness || null;
      setReadiness(nextReadiness);

      const readinessAvailable = Number.isFinite(Number(nextReadiness?.requiredCount));
      if (!readinessAvailable && needsMissionFallback && missionResults.every((result) => result?.status === "fulfilled")) {
        const productsData = missionResults[0]?.value || {};
        const settingsData = missionResults[1]?.value || {};
        const progress = buildTrialMissions({
          settings: settingsData?.settings || {},
          productCount: Array.isArray(productsData?.products) ? productsData.products.length : 0,
          modules,
        });
        setMissions(progress.missions.filter((mission) => !mission.done).slice(0, 3));
      } else {
        setMissions([]);
      }
    } finally {
      if (requestVersionRef.current === requestVersion) setLoading(false);
    }
  }, [activeProject, key, modules, needsMissionFallback]);

  React.useEffect(() => {
    setLastSeen(readLastSeen(activeProject));
    setReadiness(activeProject?.readiness || null);
    load();
  }, [activeProject, load]);

  React.useEffect(() => {
    if (!open) return;
    load();
  }, [load, open]);

  const tasks = React.useMemo(() => {
    if (!activeProject && !superAdmin) {
      return [{ id: "create-project", label: "Crear tu primer proyecto", description: "Empezá con los datos esenciales del negocio.", path: "/dashboard/proyectos" }];
    }

    const readinessAvailable = Number.isFinite(Number(readiness?.requiredCount));
    if (activeProject && readinessAvailable) {
      return buildProjectActionTasks({ project: activeProject, readiness, modules });
    }

    return missions.map((mission) => ({
      id: mission.id,
      label: mission.label,
      description: mission.description,
      path: missionRoute(activeProject, mission),
    }));
  }, [activeProject, missions, modules, readiness, superAdmin]);

  const notices = React.useMemo(() => {
    if (!activeProject) return [];
    const base = `/dashboard/proyectos/${key}`;
    const items = [];
    if (billingAccess.needsPaymentAction) {
      items.push({
        id: `billing-${billingAccess.reason}`,
        severity: "warning",
        label: billingAccess.isGrace ? "Hay un pago pendiente" : "Las funciones operativas están pausadas",
        description: billingAccess.isGrace ? "Resolvelo antes de que termine el período de gracia." : "La página pública puede seguir disponible, pero la operación requiere reactivación.",
        path: `${base}/configuracion/suscripcion`,
      });
    } else if (billingAccess.isTrial && billingAccess.trialDaysRemaining !== null && billingAccess.trialDaysRemaining <= 3) {
      items.push({
        id: "trial-ending",
        severity: "info",
        label: "La prueba está por finalizar",
        description: billingAccess.trialDaysRemaining === 1 ? "Queda 1 día de prueba." : `Quedan ${billingAccess.trialDaysRemaining} días de prueba.`,
        path: `${base}/configuracion/suscripcion`,
      });
    }
    return items;
  }, [activeProject, billingAccess, key]);

  const operationalEvents = React.useMemo(
    () => events.filter(isOperationalEvent).slice(0, 8),
    [events],
  );

  const viewCounts = React.useMemo(() => ({
    tasks: tasks.length,
    notices: notices.length,
    updates: operationalEvents.length,
  }), [notices.length, operationalEvents.length, tasks.length]);

  const unreadEvents = React.useMemo(
    () => operationalEvents.filter((event) => {
      const notable = event?.notifyProjectOwners === true || ["warning", "critical"].includes(String(event?.severity || "").toLowerCase());
      return notable && eventTimestamp(event) > lastSeen;
    }),
    [lastSeen, operationalEvents],
  );

  const pendingCount = tasks.length + notices.length + unreadEvents.length;
  const badgeContent = pendingCount > 99 ? "99+" : pendingCount;

  const handleOpen = (event) => {
    const preferredView = tasks.length
      ? "tasks"
      : notices.length
        ? "notices"
        : "updates";
    setView((current) => viewCounts[current] ? current : preferredView);
    setAnchorEl(event.currentTarget);
    writeLastSeen(activeProject);
    setLastSeen(Date.now());
  };

  const go = (path) => {
    setAnchorEl(null);
    if (path) navigate(path);
  };

  const ariaLabel = pendingCount
    ? `Actividad: ${pendingCount} elemento${pendingCount === 1 ? "" : "s"} pendiente${pendingCount === 1 ? "" : "s"}`
    : "Actividad: sin elementos pendientes";

  return (
    <>
      <Tooltip title="Actividad">
        <IconButton
          onClick={handleOpen}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open ? "true" : undefined}
          size={mobile ? "small" : "medium"}
          sx={{
            width: mobile ? 38 : 42,
            height: mobile ? 38 : 42,
            color: BRAND.dark,
            border: mobile ? 0 : "1px solid rgba(25,36,47,.09)",
            bgcolor: mobile ? "transparent" : "#fff",
            "&:hover": { bgcolor: "rgba(243,90,58,.07)", color: BRAND.orange },
          }}
        >
          <Badge color="error" badgeContent={badgeContent} invisible={!pendingCount} max={99}>
            {open ? <NotificationsRoundedIcon /> : <NotificationsNoneRoundedIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: { xs: "calc(100vw - 24px)", sm: PANEL_WIDTH },
            maxWidth: PANEL_WIDTH,
            maxHeight: "min(620px, calc(100vh - 86px))",
            borderRadius: 3,
            border: "1px solid rgba(25,36,47,.08)",
            boxShadow: "0 22px 60px rgba(25,36,47,.18)",
            overflow: "hidden",
          },
        }}
      >
        <Box role="dialog" aria-label="Actividad del panel">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.55 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: BRAND.dark, fontWeight: 1000, lineHeight: 1.15 }}>Centro de actividad</Typography>
              <Typography variant="caption" noWrap sx={{ display: "block", color: "rgba(25,36,47,.55)", fontWeight: 750 }}>
                {activeProject?.name || "Tu cuenta"}
              </Typography>
            </Box>
            <Tooltip title="Actualizar">
              <span>
                <IconButton size="small" onClick={load} disabled={loading} aria-label="Actualizar actividad">
                  {loading ? <CircularProgress size={18} /> : <RefreshRoundedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Divider />

          <Tabs
            value={view}
            onChange={(_, nextView) => setView(nextView)}
            variant="fullWidth"
            aria-label="Secciones del centro de actividad"
            sx={{
              minHeight: 42,
              borderBottom: "1px solid rgba(25,36,47,.08)",
              "& .MuiTab-root": {
                minHeight: 42,
                minWidth: 0,
                px: .5,
                textTransform: "none",
                fontSize: ".73rem",
                fontWeight: 900,
                color: "rgba(25,36,47,.58)",
              },
              "& .Mui-selected": { color: `${BRAND.orange} !important` },
              "& .MuiTabs-indicator": { bgcolor: BRAND.orange, height: 3 },
            }}
          >
            {ACTIVITY_VIEWS.map((item) => (
              <Tab
                key={item.id}
                value={item.id}
                label={`${item.label}${viewCounts[item.id] ? ` · ${viewCounts[item.id]}` : ""}`}
              />
            ))}
          </Tabs>

          <Box sx={{ maxHeight: "min(500px, calc(100vh - 192px))", minHeight: 155, overflowY: "auto", px: 1.5, py: 1.5 }}>
            {view === "tasks" && tasks.length ? (
              <Box>
                {sectionTitle(<TaskAltRoundedIcon sx={{ fontSize: 18, color: BRAND.green }} />, "TAREAS PENDIENTES", tasks.length)}
                <Typography sx={{ mt: .45, mb: .75, fontSize: ".69rem", lineHeight: 1.35, color: "rgba(25,36,47,.5)" }}>
                  Se calculan con el estado real del proyecto y desaparecen cuando el sistema verifica que se completaron.
                </Typography>
                <List disablePadding>
                  {tasks.map((task, index) => (
                    <ListItemButton key={task.id} onClick={() => go(task.path)} sx={{ borderRadius: 2, px: 1.1, py: .85, alignItems: "flex-start" }}>
                      <ListItemText
                        primary={task.label}
                        secondary={task.description}
                        primaryTypographyProps={{ sx: { fontSize: ".83rem", fontWeight: 900, color: BRAND.dark } }}
                        secondaryTypographyProps={{ sx: { mt: .2, fontSize: ".72rem", lineHeight: 1.35, color: "rgba(25,36,47,.55)" } }}
                      />
                      <Stack alignItems="flex-end" spacing={.45} sx={{ mt: .1, ml: .7, flexShrink: 0 }}>
                        <Chip
                          size="small"
                          label={index === 0 ? "Siguiente" : "Pendiente"}
                          color={index === 0 ? "warning" : "default"}
                          sx={{ height: 19, fontSize: ".58rem", fontWeight: 900 }}
                        />
                        <ArrowForwardRoundedIcon sx={{ fontSize: 17, color: "rgba(25,36,47,.34)" }} />
                      </Stack>
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            ) : null}

            {view === "notices" && notices.length ? (
              <Box>
                {sectionTitle(<WarningAmberRoundedIcon sx={{ fontSize: 18, color: BRAND.orange }} />, "AVISOS QUE REQUIEREN ATENCIÓN", notices.length)}
                <Typography sx={{ mt: .45, mb: .85, fontSize: ".69rem", lineHeight: 1.35, color: "rgba(25,36,47,.5)" }}>
                  Acá se agrupan estados vigentes que pueden afectar la continuidad del negocio.
                </Typography>
                <Stack spacing={.75}>
                  {notices.map((notice) => (
                    <Alert
                      key={notice.id}
                      severity={notice.severity}
                      action={<Button color="inherit" size="small" onClick={() => go(notice.path)} sx={{ fontWeight: 900 }}>Resolver</Button>}
                      sx={{ borderRadius: 2, py: .35, "& .MuiAlert-message": { minWidth: 0 } }}
                    >
                      <Stack direction="row" spacing={.7} alignItems="center" sx={{ mb: .15 }}>
                        <Typography sx={{ fontSize: ".78rem", fontWeight: 900 }}>{notice.label}</Typography>
                        <Chip size="small" label="Atención" sx={{ height: 18, fontSize: ".56rem", fontWeight: 900 }} />
                      </Stack>
                      <Typography sx={{ fontSize: ".7rem", lineHeight: 1.35 }}>{notice.description}</Typography>
                    </Alert>
                  ))}
                </Stack>
              </Box>
            ) : null}

            {view === "updates" && operationalEvents.length ? (
              <Box>
                {sectionTitle(<HistoryRoundedIcon sx={{ fontSize: 18, color: "rgba(25,36,47,.6)" }} />, "NOVEDADES OPERATIVAS", operationalEvents.length)}
                <Typography sx={{ mt: .45, mb: .75, fontSize: ".69rem", lineHeight: 1.35, color: "rgba(25,36,47,.5)" }}>
                  Pedidos, reservas, cobros y otros movimientos recientes se muestran separados de las tareas y avisos.
                </Typography>
                <List disablePadding>
                  {operationalEvents.map((event) => {
                    const state = eventState(event);
                    return (
                      <ListItemButton key={event.id || `${event.action}-${event.occurredAt}`} onClick={() => go(eventRoute(activeProject, event))} sx={{ borderRadius: 2, px: 1.1, py: .8, alignItems: "flex-start" }}>
                        <ListItemText
                          primary={event.title || categoryLabel(event.category)}
                          secondary={event.description || categoryLabel(event.category)}
                          primaryTypographyProps={{ sx: { fontSize: ".8rem", lineHeight: 1.28, fontWeight: 850, color: BRAND.dark } }}
                          secondaryTypographyProps={{ sx: { mt: .15, fontSize: ".69rem", lineHeight: 1.35, color: "rgba(25,36,47,.5)" } }}
                        />
                        <Stack alignItems="flex-end" spacing={.35} sx={{ ml: 1, flexShrink: 0 }}>
                          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.42)", fontSize: ".61rem", fontWeight: 750 }}>
                            {relativeTime(event.occurredAt)}
                          </Typography>
                          <Chip
                            size="small"
                            label={state.label}
                            color={state.color}
                            sx={{ height: 18, fontSize: ".56rem", fontWeight: 900 }}
                          />
                          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.45)", fontSize: ".57rem", fontWeight: 800 }}>
                            {OPERATIONAL_CATEGORY_LABELS[String(event?.category || "").toLowerCase()] || categoryLabel(event.category)} · prioridad {state.priority.toLowerCase()}
                          </Typography>
                        </Stack>
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            ) : null}

            {!viewCounts[view] ? (
              <Stack alignItems="center" justifyContent="center" spacing={.75} sx={{ minHeight: 135, textAlign: "center", px: 2 }}>
                {view === "tasks" ? <TaskAltRoundedIcon sx={{ color: BRAND.green }} /> : view === "notices" ? <WarningAmberRoundedIcon sx={{ color: "rgba(25,36,47,.35)" }} /> : <HistoryRoundedIcon sx={{ color: "rgba(25,36,47,.35)" }} />}
                <Typography sx={{ color: "rgba(25,36,47,.55)", fontSize: ".76rem", lineHeight: 1.4 }}>
                  {emptyMessage(view, activeProject, loading)}
                </Typography>
              </Stack>
            ) : null}
          </Box>

          {activeProject ? (
            <>
              <Divider />
              <Button fullWidth onClick={() => go(`/dashboard/proyectos/${key}/actividad`)} sx={{ py: 1.1, borderRadius: 0, textTransform: "none", fontWeight: 900, color: BRAND.orange }}>
                Ver historial completo
              </Button>
            </>
          ) : null}
        </Box>
      </Popover>
    </>
  );
}
